// useTheme — system-following dark mode for the web app (per ADR-0009).
//
// Theme follows the OS by default. A user override is persisted in
// localStorage and applied by setting `data-theme` on <html>:
//   - 'system' → no attribute pin; tokens.css honours `prefers-color-scheme`
//   - 'light'  → data-theme="light"  (forces the light bundle)
//   - 'dark'   → data-theme="dark"   (forces the dark bundle)
//
// The colour values themselves live in src/styles/tokens.css — this module only
// decides which bundle is active. `applyStoredTheme()` runs once before React
// mounts so there's no light-then-dark flash on first paint.

import { useCallback, useEffect, useState } from 'react';

export type ThemePreference = 'system' | 'light' | 'dark';

const STORAGE_KEY = 'jotly.themePreference';

function readStored(): ThemePreference {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
  } catch {
    // localStorage unavailable (private mode / SSR) — fall through to system.
  }
  return 'system';
}

function apply(pref: ThemePreference) {
  const root = document.documentElement;
  if (pref === 'system') {
    // Remove the pin so the prefers-color-scheme media query takes over.
    root.removeAttribute('data-theme');
  } else {
    root.setAttribute('data-theme', pref);
  }
}

/** Apply the persisted preference to <html> immediately, before React mounts. */
export function applyStoredTheme(): void {
  apply(readStored());
}

/** Resolve which bundle is actually showing right now (system → OS query). */
function resolveScheme(pref: ThemePreference): 'light' | 'dark' {
  if (pref !== 'system') return pref;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface UseThemeResult {
  preference: ThemePreference;
  scheme: 'light' | 'dark';
  setPreference: (p: ThemePreference) => void;
}

export function useTheme(): UseThemeResult {
  const [preference, setPreferenceState] = useState<ThemePreference>(readStored);
  const [scheme, setScheme] = useState<'light' | 'dark'>(() => resolveScheme(readStored()));

  // Re-resolve the active bundle when the preference changes or — while on
  // 'system' — when the OS flips appearance.
  useEffect(() => {
    setScheme(resolveScheme(preference));
    if (preference !== 'system') return;
    const mq = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!mq) return;
    const onChange = () => setScheme(resolveScheme('system'));
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [preference]);

  const setPreference = useCallback((p: ThemePreference) => {
    setPreferenceState(p);
    apply(p);
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      // Non-critical — losing the write just means the next load follows the OS.
    }
  }, []);

  return { preference, scheme, setPreference };
}
